import React, { useMemo } from "react";
import EChart from "../../components/Charts/Echart";

const CHART_COLORS = ["#327ec4", "#15a37f", "#f59e0b", "#7c3aed", "#ef4444"];

const humanize = (value) => String(value || "")
  .replace(/_/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .replace(/^./, (letter) => letter.toLocaleUpperCase("es-AR"));

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

export const sanitizeReportCharts = (charts) => (Array.isArray(charts) ? charts : [])
  .map((chart) => {
    const series = (Array.isArray(chart?.series) ? chart.series : [])
      .map((point) => ({
        label: humanize(point?.label),
        value: finiteNumber(point?.value),
      }))
      .filter((point) => point.label && point.value !== null)
      .slice(0, 12);
    return {
      key: String(chart?.key || chart?.title || "").slice(0, 80),
      type: chart?.type === "progress" ? "progress" : "bar",
      title: humanize(chart?.title || "Gráfico"),
      unit: String(chart?.unit || "").replace(/_/g, " ").trim(),
      series,
    };
  })
  .filter((chart) => chart.key && chart.series.length > 0)
  .slice(0, 6);

const formatNumber = (value, unit = "") => {
  const decimals = Math.abs(value) >= 100 || unit === "%" ? 0 : 2;
  const text = new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  }).format(value);
  return unit ? `${text} ${unit}` : text;
};

export const buildChartOption = (chart) => {
  const labels = chart.series.map((point) => point.label);
  const values = chart.series.map((point) => point.value);
  const horizontal = chart.type === "progress" || labels.some((label) => label.length > 18);
  const maxValue = chart.type === "progress" ? 100 : undefined;

  return {
    animation: false,
    grid: {
      top: 18,
      right: 18,
      bottom: horizontal ? 18 : 44,
      left: horizontal ? 96 : 46,
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (items) => {
        const item = Array.isArray(items) ? items[0] : items;
        return `${item?.name || ""}<br/>${formatNumber(Number(item?.value || 0), chart.unit)}`;
      },
    },
    xAxis: horizontal
      ? {
          type: "value",
          max: maxValue,
          axisLabel: { color: "#607085", fontSize: 10 },
          splitLine: { lineStyle: { color: "#edf2f7" } },
        }
      : {
          type: "category",
          data: labels,
          axisTick: { show: false },
          axisLabel: { color: "#607085", fontSize: 10, interval: 0 },
        },
    yAxis: horizontal
      ? {
          type: "category",
          data: labels,
          axisTick: { show: false },
          axisLabel: { color: "#607085", fontSize: 10 },
        }
      : {
          type: "value",
          axisLabel: { color: "#607085", fontSize: 10 },
          splitLine: { lineStyle: { color: "#edf2f7" } },
        },
    series: [
      {
        type: "bar",
        data: values,
        barMaxWidth: 34,
        itemStyle: {
          borderRadius: horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0],
          color: (params) => CHART_COLORS[params.dataIndex % CHART_COLORS.length],
        },
        label: {
          show: chart.type === "progress",
          position: "right",
          formatter: (params) => formatNumber(Number(params.value), chart.unit),
          color: "#30445c",
          fontSize: 10,
        },
      },
    ],
  };
};

const ConsultantReportChart = ({ chart }) => {
  const option = useMemo(() => buildChartOption(chart), [chart]);
  const height = chart.type === "progress" ? 120 : Math.max(170, chart.series.length * 34);

  return (
    <section className="consultant-report-chart" aria-label={chart.title}>
      <div className="consultant-report-chart__header">
        <h3>{chart.title}</h3>
        {chart.unit && <span>{chart.unit}</span>}
      </div>
      <EChart
        option={option}
        className="consultant-report-chart__canvas"
        style={{ width: "100%", height }}
      />
    </section>
  );
};

const ConsultantReportCharts = ({ charts }) => {
  const sanitized = useMemo(() => sanitizeReportCharts(charts), [charts]);
  if (sanitized.length === 0) return null;

  return (
    <div className="consultant-report-charts" aria-label="Gráficos del reporte">
      {sanitized.map((chart) => (
        <ConsultantReportChart key={chart.key} chart={chart} />
      ))}
    </div>
  );
};

export default ConsultantReportCharts;
